import { useState, useEffect, useMemo } from 'react'

function pad(value) {
  return String(value).padStart(2, '0')
}

export function formatIsoDate(date) {
  if (!date || Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function parseIsoDate(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value !== 'string') return null
  const clean = value.split('T')[0]
  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return null
  const [year, month, day] = clean.split('-').map(Number)
  const parsed = new Date(year, month - 1, day)
  if (Number.isNaN(parsed.getTime())) return null
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null
  }
  return parsed
}

const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

const WEEKDAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/**
 * Windows Taskbar-style Mini Calendar Component
 * Features the signature 3-tier Windows navigation:
 *   Level 1: Days View (7x6 month grid with outside days, today indicator, selection)
 *   Level 2: Month View (4x3 year grid for rapid month switching)
 *   Level 3: Decade View (4x3 decade grid for rapid year switching)
 */
export function Calendar({
  selected,
  onSelect,
  month,
  onMonthChange,
  disabled,
  min,
  max,
  onClear,
  onToday,
  className = '',
  showFooter = true,
}) {
  const today = useMemo(() => new Date(), [])
  const selectedDate = useMemo(() => parseIsoDate(selected), [selected])
  const minDate = useMemo(() => parseIsoDate(min), [min])
  const maxDate = useMemo(() => parseIsoDate(max), [max])

  // View mode: 'days' | 'months' | 'years'
  const [viewMode, setViewMode] = useState('days')

  // Cursor date for what month/year/decade we are browsing
  const [cursorDate, setCursorDate] = useState(() => {
    if (month && parseIsoDate(month)) return parseIsoDate(month)
    if (selectedDate) return selectedDate
    return today
  })

  // Sync cursor when controlled month prop changes
  useEffect(() => {
    if (month) {
      const parsed = parseIsoDate(month)
      if (parsed) setCursorDate(parsed)
    }
  }, [month])

  const cursorYear = cursorDate.getFullYear()
  const cursorMonth = cursorDate.getMonth()

  const isDateDisabled = (date) => {
    if (!date) return false
    if (disabled && typeof disabled === 'function' && disabled(date)) return true
    const iso = formatIsoDate(date)
    if (minDate && iso < formatIsoDate(minDate)) return true
    if (maxDate && iso > formatIsoDate(maxDate)) return true
    return false
  }

  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    )
  }

  const isSameMonth = (d1, d2) => {
    if (!d1 || !d2) return false
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth()
  }

  const isSameYear = (d1, d2) => {
    if (!d1 || !d2) return false
    return d1.getFullYear() === d2.getFullYear()
  }

  // Update cursor and inform parent if needed
  const updateCursor = (newDate) => {
    setCursorDate(newDate)
    onMonthChange?.(newDate)
  }

  // Navigation handlers
  const handlePrev = (e) => {
    e?.stopPropagation()
    if (viewMode === 'days') {
      updateCursor(new Date(cursorYear, cursorMonth - 1, 1))
    } else if (viewMode === 'months') {
      updateCursor(new Date(cursorYear - 1, cursorMonth, 1))
    } else if (viewMode === 'years') {
      updateCursor(new Date(cursorYear - 10, cursorMonth, 1))
    }
  }

  const handleNext = (e) => {
    e?.stopPropagation()
    if (viewMode === 'days') {
      updateCursor(new Date(cursorYear, cursorMonth + 1, 1))
    } else if (viewMode === 'months') {
      updateCursor(new Date(cursorYear + 1, cursorMonth, 1))
    } else if (viewMode === 'years') {
      updateCursor(new Date(cursorYear + 10, cursorMonth, 1))
    }
  }

  // Header click: drill upwards
  const handleHeaderClick = () => {
    if (viewMode === 'days') {
      setViewMode('months')
    } else if (viewMode === 'months') {
      setViewMode('years')
    } else {
      setViewMode('days')
    }
  }

  // 42-day matrix for Days View
  const daysMatrix = useMemo(() => {
    const firstDayOfWeek = new Date(cursorYear, cursorMonth, 1).getDay() // 0 = Sun
    const totalDaysInMonth = new Date(cursorYear, cursorMonth + 1, 0).getDate()
    const prevMonthTotalDays = new Date(cursorYear, cursorMonth, 0).getDate()

    const list = []

    // Trailing days from previous month
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const d = prevMonthTotalDays - i
      list.push({
        date: new Date(cursorYear, cursorMonth - 1, d),
        dayNum: d,
        isOutside: true,
        isPrevMonth: true,
      })
    }

    // Days in current month
    for (let d = 1; d <= totalDaysInMonth; d++) {
      list.push({
        date: new Date(cursorYear, cursorMonth, d),
        dayNum: d,
        isOutside: false,
      })
    }

    // Leading days from next month (fill up to 42 cells = 6 full rows)
    const remaining = 42 - list.length
    for (let d = 1; d <= remaining; d++) {
      list.push({
        date: new Date(cursorYear, cursorMonth + 1, d),
        dayNum: d,
        isOutside: true,
        isNextMonth: true,
      })
    }

    return list
  }, [cursorYear, cursorMonth])

  // Decade years for Years View
  const decadeStart = Math.floor(cursorYear / 10) * 10
  const decadeYears = useMemo(() => {
    const list = []
    // 12 cells: 1 outside prev year, 10 decade years, 1 outside next year
    for (let y = decadeStart - 1; y <= decadeStart + 10; y++) {
      list.push({
        year: y,
        isOutside: y < decadeStart || y > decadeStart + 9,
      })
    }
    return list
  }, [decadeStart])

  // Select a day
  const handleDayClick = (item) => {
    if (isDateDisabled(item.date)) return
    if (item.isOutside) {
      updateCursor(new Date(item.date.getFullYear(), item.date.getMonth(), 1))
    }
    onSelect?.(item.date)
  }

  // Select a month from Month View
  const handleMonthClick = (mIndex) => {
    updateCursor(new Date(cursorYear, mIndex, 1))
    setViewMode('days')
  }

  // Select a year from Decade View
  const handleYearClick = (yearNum) => {
    updateCursor(new Date(yearNum, cursorMonth, 1))
    setViewMode('months')
  }

  // Header Title text
  let headerTitle = ''
  if (viewMode === 'days') {
    headerTitle = `${MONTH_NAMES_FULL[cursorMonth]} ${cursorYear}`
  } else if (viewMode === 'months') {
    headerTitle = `${cursorYear}`
  } else {
    headerTitle = `${decadeStart} – ${decadeStart + 9}`
  }

  return (
    <div className={`windows-mini-calendar select-none font-sans text-on-surface ${className}`}>
      {/* ── Top Header with drill-down button & Nav Arrows ── */}
      <div className="flex items-center justify-between pb-2 mb-1 border-b border-outline-variant/20">
        <button
          type="button"
          onClick={handleHeaderClick}
          className="group flex items-center gap-1 px-2 py-1 -ml-1 text-xs font-bold text-on-surface hover:text-primary hover:bg-surface-container-low rounded-lg transition-colors cursor-pointer"
          title={`Click to switch to ${viewMode === 'days' ? 'Months' : viewMode === 'months' ? 'Years' : 'Days'} view`}
        >
          <span className="text-[13px] font-bold tracking-tight text-on-surface group-hover:text-primary transition-colors">
            {headerTitle}
          </span>
          <span className="material-symbols-outlined text-[16px] text-on-surface-variant group-hover:text-primary transition-transform group-hover:scale-110">
            {viewMode === 'years' ? 'unfold_more' : 'expand_more'}
          </span>
        </button>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handlePrev}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-on-surface-variant hover:text-primary hover:bg-surface-container-low active:scale-95 transition cursor-pointer"
            aria-label="Previous"
            title="Previous"
          >
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-on-surface-variant hover:text-primary hover:bg-surface-container-low active:scale-95 transition cursor-pointer"
            aria-label="Next"
            title="Next"
          >
            <span className="material-symbols-outlined text-[18px]">chevron_right</span>
          </button>
        </div>
      </div>

      {/* ── Level 1: Days View ── */}
      {viewMode === 'days' && (
        <div className="flex flex-col">
          {/* Weekday abbreviations */}
          <div className="grid grid-cols-7 text-center py-1">
            {WEEKDAY_NAMES.map((name) => (
              <span
                key={name}
                className="text-[11px] font-bold tracking-wider text-on-surface-variant/75 uppercase"
              >
                {name}
              </span>
            ))}
          </div>

          {/* 42-day cells grid (6 weeks x 7 days) */}
          <div className="grid grid-cols-7 gap-y-0.5 gap-x-0.5 place-items-center pt-0.5">
            {daysMatrix.map((item, index) => {
              const disabledDay = isDateDisabled(item.date)
              const isSelected = selectedDate && isSameDay(item.date, selectedDate)
              const isToday = isSameDay(item.date, today)

              let btnClasses =
                'relative flex h-8 w-8 items-center justify-center rounded-lg text-xs font-semibold transition-all cursor-pointer'

              if (disabledDay) {
                btnClasses += ' text-on-surface-variant/25 cursor-not-allowed pointer-events-none'
              } else if (isSelected) {
                btnClasses += ' bg-primary text-on-primary font-bold shadow-xs hover:bg-primary-container'
              } else if (isToday) {
                btnClasses +=
                  ' ring-1.5 ring-inset ring-primary text-primary font-bold hover:bg-primary/10'
              } else if (item.isOutside) {
                btnClasses +=
                  ' text-on-surface-variant/40 hover:text-on-surface hover:bg-surface-container-low'
              } else {
                btnClasses +=
                  ' text-on-surface hover:bg-surface-container-low active:bg-surface-container-high'
              }

              return (
                <button
                  key={`${item.date.toISOString()}-${index}`}
                  type="button"
                  onClick={() => handleDayClick(item)}
                  disabled={disabledDay}
                  className={btnClasses}
                >
                  {item.dayNum}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Level 2: Month View (4x3 grid) ── */}
      {viewMode === 'months' && (
        <div className="grid grid-cols-4 gap-2 py-2">
          {MONTH_NAMES_SHORT.map((name, index) => {
            const isSelected =
              selectedDate &&
              selectedDate.getFullYear() === cursorYear &&
              selectedDate.getMonth() === index
            const isCurrentMonth = today.getFullYear() === cursorYear && today.getMonth() === index

            let btnClasses =
              'flex h-11 items-center justify-center rounded-xl text-xs font-semibold transition-all cursor-pointer'

            if (isSelected) {
              btnClasses += ' bg-primary text-on-primary font-bold shadow-xs hover:bg-primary-container'
            } else if (isCurrentMonth) {
              btnClasses +=
                ' ring-1.5 ring-inset ring-primary text-primary font-bold hover:bg-primary/10'
            } else {
              btnClasses +=
                ' text-on-surface hover:bg-surface-container-low active:bg-surface-container-high'
            }

            return (
              <button
                key={name}
                type="button"
                onClick={() => handleMonthClick(index)}
                className={btnClasses}
              >
                {name}
              </button>
            )
          })}
        </div>
      )}

      {/* ── Level 3: Decade / Year View (4x3 grid) ── */}
      {viewMode === 'years' && (
        <div className="grid grid-cols-4 gap-2 py-2">
          {decadeYears.map((item) => {
            const isSelected = selectedDate && selectedDate.getFullYear() === item.year
            const isCurrentYear = today.getFullYear() === item.year

            let btnClasses =
              'flex h-11 items-center justify-center rounded-xl text-xs font-semibold transition-all cursor-pointer'

            if (isSelected) {
              btnClasses += ' bg-primary text-on-primary font-bold shadow-xs hover:bg-primary-container'
            } else if (isCurrentYear) {
              btnClasses +=
                ' ring-1.5 ring-inset ring-primary text-primary font-bold hover:bg-primary/10'
            } else if (item.isOutside) {
              btnClasses +=
                ' text-on-surface-variant/40 hover:text-on-surface hover:bg-surface-container-low'
            } else {
              btnClasses +=
                ' text-on-surface hover:bg-surface-container-low active:bg-surface-container-high'
            }

            return (
              <button
                key={item.year}
                type="button"
                onClick={() => handleYearClick(item.year)}
                className={btnClasses}
              >
                {item.year}
              </button>
            )
          })}
        </div>
      )}

      {/* ── Footer Controls: Today & Clear ── */}
      {showFooter && (
        <div className="mt-2 flex items-center justify-between border-t border-outline-variant/20 pt-2">
          <button
            type="button"
            onClick={onClear}
            className="rounded-md px-2.5 py-1 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition cursor-pointer"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onToday}
            className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-bold text-primary hover:bg-primary/10 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-[14px]">today</span>
            <span>Today</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default Calendar
